import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  getArrayLength,
  getCanShareNode,
  getIsArrayEmpty,
  getIsFolderEmpty,
  getIsNodeFile,
} from 'utils';
import DealContext from '../../../providers/Deal';
import { getIsNodeDisabled, getNodeLockStages } from '../utils/stageFiles';

const STAGES = Object.values(LEAD_STAGES);

type UseDisabledNodeReasonProps = {
  workflowId: string;
  page: LeadStages;
};

// Why a node can't be ticked on this stage; undefined when it can.
const useDisabledNodeReason = ({
  workflowId,
  page,
}: UseDisabledNodeReasonProps) => {
  const [stageByFileId, setStageByFileId] = useState<
    Record<string, LeadStages>
  >({});

  const { popServerError } = useContext(PopNotificationsContext);
  const { leadStagesCopies } = useContext(DealContext);

  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    const earlierStages = STAGES.slice(0, STAGES.indexOf(page));

    Promise.all(
      earlierStages.map((stage) =>
        api.portfolio.seller.getWorkflowConfiguration(workflowId, stage),
      ),
    )
      .then((responses) => {
        if (cancelled) return;

        // Ticks carry over to later stages, so a file belongs to the earliest one listing it.
        const stages: Record<string, LeadStages> = {};

        responses.forEach(({ data }, index) =>
          (data.checkedFilesIds || []).forEach((id) => {
            if (!stages[id]) stages[id] = earlierStages[index];
          }),
        );

        setStageByFileId(stages);
      })
      .catch(popServerError);

    return () => {
      cancelled = true;
    };
  }, [page, popServerError, workflowId]);

  return useCallback(
    (node: DataRoomNode) => {
      if (!getIsNodeDisabled(node)) return undefined;

      if (getIsFolderEmpty(node)) return t('emptyFolder');

      if (!getCanShareNode(node)) return t('buySideFilesCannotBeShared');

      const names = getNodeLockStages(node, stageByFileId).map((stage) =>
        t(leadStagesCopies[stage]),
      );

      if (getIsArrayEmpty(names)) return t('sharedInEarlierStage');

      if (getIsNodeFile(node))
        return t('fileSharedInStage', { stage: names[0] });

      const lastIndex = getArrayLength(names) - 1;

      return t('folderFilesSharedInStages', {
        count: getArrayLength(names),
        stages: lastIndex
          ? `${names.slice(0, lastIndex).join(', ')} ${t('and')} ${
              names[lastIndex]
            }`
          : names[0],
      });
    },
    [leadStagesCopies, stageByFileId, t],
  );
};

export default useDisabledNodeReason;
